import React, { useCallback } from 'react';
import { FormattedMessage } from 'react-intl-next';
import { useFlags } from '@atlaskit/flag';
import { type ActionsType } from '@atlaskit/flag/types';
import { token } from '@atlaskit/tokens';
import { G300 as successColor, Y300 as primaryColour } from '@atlaskit/theme/colors';
import messages from './messages';
import { useAnalyticsEvents } from '@atlaskit/analytics-next';
import { fireUIAnalytics } from '@atlassian/analytics-bridge';
import SuccessIcon from '@atlaskit/icon/core/migration/success--check-circle';
import WarningIcon from '@atlaskit/icon/core/migration/warning';
import { messages as baseMessages } from '../../../view/flags/index';

const withAutoDismissFlagArgs = {
	isAutoDismiss: true,
};

const baseSuccessFlagArgs = {
	...withAutoDismissFlagArgs,
	icon: (
		<SuccessIcon label="Success" spacing="spacious" color={token('color.icon', successColor)} />
	),
};

const baseErrorFlagArgs = {
	...withAutoDismissFlagArgs,
	icon: (
		<WarningIcon label="Warning" spacing="spacious" color={token('color.icon', primaryColour)} />
	),
};

export const useEmbeddedPagesFlags = () => {
	const { showFlag } = useFlags();
	const { createAnalyticsEvent } = useAnalyticsEvents();

	const showPagePublishedFlag = useCallback(() => {
		const analyticsEventFlag = createAnalyticsEvent({
			action: 'viewed',
			actionSubject: 'flag',
		});
		fireUIAnalytics(analyticsEventFlag, 'embeddedPagePublishSuccessFlag');
		showFlag({
			...baseSuccessFlagArgs,
			id: 'pagePublishedFlag',
			title: <FormattedMessage {...messages.pagePublishedFlagTitle} />,
			analyticsContext: {},
			testId: 'confluence-project-pages.flag.page-published',
		});
	}, [createAnalyticsEvent, showFlag]);

	const showDraftSavedFlag = useCallback(
		(onOpenDraft?: () => void) => {
			const actions: ActionsType = onOpenDraft
				? [
						{
							content: <FormattedMessage {...messages.draftSavedFlagOpenDraft} />,
							onClick: onOpenDraft,
						},
					]
				: [];
			return showFlag({
				...baseSuccessFlagArgs,
				id: 'draftSavedFlag',
				title: <FormattedMessage {...messages.draftSavedFlagTitle} />,
				description: <FormattedMessage {...messages.draftSavedFlagDescription} />,
				actions,
				testId: 'confluence-project-pages.flag.draft-saved',
			});
		},
		[showFlag],
	);

	const showPageDeletedFlag = useCallback(
		() =>
			showFlag({
				...baseSuccessFlagArgs,
				id: 'pageDeletedFlag',
				title: <FormattedMessage {...messages.pageDeletedFlagTitle} />,
				testId: 'confluence-project-pages.flag.page-deleted',
			}),
		[showFlag],
	);

	const showErrorFlag = useCallback(
		() =>
			showFlag({
				...baseErrorFlagArgs,
				id: 'pageErrorFlag',
				title: <FormattedMessage {...baseMessages['generic-error-heading']} />,
				description: <FormattedMessage {...baseMessages['generic-error-description']} />,
				testId: 'confluence-project-pages.flag.error',
			}),
		[showFlag],
	);

	return {
		showPagePublishedFlag,
		showDraftSavedFlag,
		showPageDeletedFlag,
		showErrorFlag,
	};
};
